import { CustomGetProjectMapQuery } from "generated/graphql";
import { FeatureCollection } from "geojson";
import useSyncProjectMapMeasurements from "hooks/projectMaps/useSyncProjectMapMeasurements";
import useMapMeasurements from "hooks/mapMeasurements/useMapMeasurements";
import { useCallback } from "react";
import {
  ExistingStageMeasurement,
  normalizeStageMapData,
  StageMeasurementDraft,
} from "modules/proposals/draft/measurements/stageMeasurements";

type UseProjectMapMeasurementsArgs = {
  projectMap: NonNullable<CustomGetProjectMapQuery["getProjectMap"]> | null;
};

export default function useProjectMapMeasurements({ projectMap }: UseProjectMapMeasurementsArgs) {
  const [syncMeasurements] = useSyncProjectMapMeasurements();

  const persistMeasurements = useCallback(
    async (nextMapData: FeatureCollection | null, nextMeasurements: StageMeasurementDraft[]) => {
      if (!projectMap?.id) return null;

      const normalizedMapData = normalizeStageMapData(nextMapData);
      if (!normalizedMapData) return null;

      return syncMeasurements({
        variables: {
          input: {
            projectMapId: projectMap.id,
            mapData: JSON.stringify(normalizedMapData),
            measurements: nextMeasurements.map((m) => ({
              geojsonFeatureId: m.featureId,
              measurementType: m.measurementType,
              measurementJob: m.productId ? m.measurementJob : null,
              measurementValue: m.productId ? m.measurementValue : null,
              featureGeojson: m.featureGeojson,
              label: m.label,
              sortOrder: m.sortOrder,
              productId: m.productId,
              isScreenshotTarget: !!m.productId && !!m.isScreenshotTarget,
            })),
          },
        },
      });
    },
    [projectMap?.id, syncMeasurements]
  );

  return useMapMeasurements({
    enabled: !!projectMap,
    resetDeps: [projectMap?.id, projectMap?.mapData, projectMap?.measurements],
    mapDataJson: projectMap?.mapData,
    existingMeasurements: (projectMap?.measurements ?? []) as ExistingStageMeasurement[],
    persistMeasurements,
    canPersist: !!projectMap?.id,
    initialDrawerOpen: true,
  });
}
